import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tournamentApi } from '../services/api-client';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { TOURNAMENT_FORMATS, TournamentFormatKey } from '../constants/tournament-formats';
import { formatDate } from '../utils/dateTime';

interface TrackWrestlingEvent {
  eventId: string;
  tournamentId?: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  url: string;
}

// US States for dropdown
const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

// Common date options for dropdown
const COMMON_DATES = [
  { label: 'This Weekend', value: 'this_weekend' },
  { label: 'Next Weekend', value: 'next_weekend' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Next Month', value: 'next_month' },
];

export function CreateTournamentPage() {
  const navigate = useNavigate();

  // Format selection - no default; user must explicitly choose
  const [selectedFormat, setSelectedFormat] = useState<TournamentFormatKey | null>(null);

  // Search fields
  const [eventName, setEventName] = useState('');
  const [commonDates, setCommonDates] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [wrestlestatUrl, setWrestlestatUrl] = useState('');
  const [scrapeWrestlestatSeeds, setScrapeWrestlestatSeeds] = useState(true);
  const [floUrl, setFloUrl] = useState('');

  // Results
  const [searchResults, setSearchResults] = useState<TrackWrestlingEvent[]>([]);
  const [showResults, setShowResults] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!eventName && !city && !state && !startDate && !endDate && !commonDates) {
      setError('Please enter at least one search criterion');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/trackwrestling/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventName: eventName || undefined,
          commonDates: commonDates || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          city: city || undefined,
          state: state || undefined,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Your session has expired. Please log out and log back in.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Search failed with status ${response.status}`);
      }

      const data = await response.json();
      setSearchResults(data.events || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search failed:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to search TrackWrestling. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = async (event: TrackWrestlingEvent) => {
    if (!selectedFormat) {
      setError('Please select a tournament format (4, 6, or 8 places) before creating.');
      return;
    }

    if (!event.startDate || !event.endDate) {
      setError('Tournament is missing date information. Please select a different tournament.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const format = TOURNAMENT_FORMATS[selectedFormat];

      // Set default roster lock time to 9 AM on the tournament start date
      // If no start date, default to 1 hour from now
      let defaultRosterLockTime;
      if (event.startDate) {
        // Parse the date and set time to 9:00 AM local time
        const date = new Date(event.startDate + 'T09:00:00');
        defaultRosterLockTime = date.toISOString();
      } else {
        const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
        defaultRosterLockTime = oneHourFromNow.toISOString();
      }

      const createInput = {
        name: event.name,
        location: event.location,
        startDate: event.startDate,
        endDate: event.endDate,
        wrestlestatUrl: wrestlestatUrl || undefined,
        scrapeWrestlestatSeeds: wrestlestatUrl ? scrapeWrestlestatSeeds : undefined,
        floUrl: floUrl || undefined,
        weightClasses: ['125', '133', '141', '149', '157', '165', '174', '184', '197', '285'], // NCAA default
        placePoints: format.placePoints,
        defaultBudget: format.recommendedBudget,
        tournamentType: 'college',
        defaultRosterLockTime,
      };

      console.log('Creating tournament with WrestleStat URL:', wrestlestatUrl);
      console.log('Full create input:', createInput);

      const tournament = await tournamentApi.create(createInput);

      console.log('Tournament created, response:', tournament);
      console.log('WrestleStat URL in response:', tournament.wrestlestatUrl);

      navigate(`/tournaments/${tournament.id}`);
    } catch (err: any) {
      console.error('Failed to create tournament:', err);
      setError(err instanceof Error ? err.message : 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileLayout>
      <PageHeader title="Create Tournament" showBack />

      <div className="p-4">
        {/* Tournament Format Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tournament Format <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {([8, 6, 4] as const).map((formatKey) => {
              const format = TOURNAMENT_FORMATS[formatKey];
              return (
                <button
                  key={formatKey}
                  type="button"
                  onClick={() => setSelectedFormat(formatKey)}
                  className={`px-4 py-3 border rounded-md font-medium transition-colors ${
                    selectedFormat === formatKey
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                  }`}
                >
                  <div>{format.label}</div>
                  <div className="text-xs mt-1 opacity-80">${format.recommendedBudget}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* WrestleStat URL (Optional) - Show before search results */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            WrestleStat Preliminary Seeds (Optional)
          </h3>
          <p className="text-xs text-blue-700 mb-3">
            Add a WrestleStat participants URL to load preliminary seeds before TrackWrestling seeds
            are available. Fill this in before selecting a tournament below.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WrestleStat Participants URL
            </label>
            <input
              type="url"
              value={wrestlestatUrl}
              onChange={(e) => setWrestlestatUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.wrestlestat.com/event/95953/participants/wrestlers"
            />
          </div>
          {wrestlestatUrl && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={scrapeWrestlestatSeeds}
                onChange={(e) => setScrapeWrestlestatSeeds(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-blue-800">
                Use WrestleStat for preliminary seed scraping
              </span>
            </label>
          )}
        </div>

        {/* FloWrestling URL (Optional) */}
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-orange-900 mb-2">
            FloWrestling Event Link (Optional)
          </h3>
          <p className="text-xs text-orange-700 mb-3">
            Add a FloWrestling event URL so users can quickly access brackets and livestreams.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              FloWrestling Event URL
            </label>
            <input
              type="url"
              value={floUrl}
              onChange={(e) => setFloUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="https://www.flowrestling.org/events/..."
            />
          </div>
        </div>

        {/* Search Results - Show at top when available */}
        {showResults && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Search Results ({searchResults.length})</h2>
              <button
                onClick={() => {
                  setShowResults(false);
                  setSearchResults([]);
                }}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Clear
              </button>
            </div>

            {searchResults.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No tournaments found. Try adjusting your search criteria below.
              </div>
            ) : (
              <div className="space-y-2">
                {searchResults.map((event, index) => (
                  <button
                    key={`${event.eventId}-${event.tournamentId}-${index}`}
                    onClick={() => handleSelectEvent(event)}
                    disabled={loading}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition-colors disabled:opacity-50"
                  >
                    <div className="font-medium">{event.name}</div>
                    {event.location && (
                      <div className="text-sm text-gray-600 mt-1">{event.location}</div>
                    )}
                    {event.startDate && event.endDate && (
                      <div className="text-sm text-gray-600 mt-1">
                        {formatDate(event.startDate)} - {formatDate(event.endDate)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search Form */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">
            {showResults ? 'Refine Search' : 'Search TrackWrestling Events'}
          </h2>

          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
              <input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., NCAA Championships"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Common Dates</label>
              <select
                value={commonDates}
                onChange={(e) => setCommonDates(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select date range...</option>
                {COMMON_DATES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Cleveland"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select state...</option>
                {US_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </form>
        </div>
      </div>
    </MobileLayout>
  );
}
